import { useSearchParams, useParams } from 'react-router-dom';
import { useConnectivityData } from '../hooks/useConnectivityData';
import { ConnectivityGraphTab } from './connectivity/ConnectivityGraphTab';
import { ConnectivitySpofTab } from './connectivity/ConnectivitySpofTab';
import { ConnectivityBandwidthTab } from './connectivity/ConnectivityBandwidthTab';
import { ConnectivityOptimizeTab } from './connectivity/ConnectivityOptimizeTab';

type TabKey = 'graph' | 'spof' | 'bandwidth' | 'optimize';

function TabButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-xs font-mono font-medium rounded-t-lg transition-colors border-t border-x ${
        active 
          ? 'bg-slate-900/80 text-cyan-400 border-slate-700/80 border-b-2 border-b-cyan-500 shadow-sm' 
          : 'bg-slate-950/40 text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-900/40'
      }`}
    >
      {label}
    </button>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="rounded-lg border border-slate-800/60 bg-slate-900/40 backdrop-blur-md p-4 flex-1 min-w-[140px] shadow-sm">
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`mt-1 text-2xl font-bold font-mono ${color || 'text-slate-100'}`}>{value}</div>
    </div>
  );
}

export default function ConnectivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabKey) || 'graph';
  const setTab = (t: TabKey) => setSearchParams({ tab: t });
  const { dcCode } = useParams<{ dcCode: string }>();

  const { graph, spof, bandwidth, handleCreateCable, handleDeleteCable } = useConnectivityData(dcCode);

  if (!dcCode) {
    return (
      <div className="min-h-full bg-slate-950 p-6">
        <div className="text-center py-12 text-slate-500 font-mono text-sm">Выберите дата-центр в верхней панели.</div>
      </div>
    );
  }

  const devices = graph?.devices ?? [];

  return (
    <div className="min-h-full bg-slate-950 text-slate-100 p-6 font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-slate-100 tracking-wide">Сетевая связность</h1>
        <p className="text-xs text-slate-400 font-mono mt-1">Анализ графа портов, обнаружение точек отказа и оптимизация маршрутизации</p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Устройства" value={graph?.summary.total_devices ?? 0} />
        <StatCard label="Порты" value={graph?.summary.total_ports ?? 0} />
        <StatCard label="Кабели" value={graph?.summary.total_cables ?? 0} />
        <StatCard label="Использование" value={`${bandwidth?.utilization_pct ?? 0}%`} color="text-cyan-400" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-0 border-b border-slate-800/60">
        <TabButton active={tab === 'graph'} label="Граф" onClick={() => setTab('graph')} />
        <TabButton active={tab === 'spof'} label="Точки отказа" onClick={() => setTab('spof')} />
        <TabButton active={tab === 'bandwidth'} label="Пропускная способность" onClick={() => setTab('bandwidth')} />
        <TabButton active={tab === 'optimize'} label="Оптимизация" onClick={() => setTab('optimize')} />
      </div>

      {/* Content */}
      <div className="bg-slate-900/40 backdrop-blur-md rounded-b-lg shadow-sm border border-t-0 border-slate-800/60 p-6 min-h-[400px]">
        {tab === 'graph' && <ConnectivityGraphTab graph={graph as any} handleCreateCable={handleCreateCable} handleDeleteCable={handleDeleteCable} />}
        {tab === 'spof' && <ConnectivitySpofTab spof={spof} />}
        {tab === 'bandwidth' && <ConnectivityBandwidthTab bandwidth={bandwidth} />}
        {tab === 'optimize' && <ConnectivityOptimizeTab dcCode={dcCode} devices={devices} />}
      </div>
    </div>
  );
}

